import { Injectable, inject } from "@angular/core";
import { Selectable } from "common/selection/state/selectable.model";
import { SelectionRepository } from "common/selection/state/selection.repository";
import { emitOnce, Store } from "@ngneat/elf";
import { getIdKeyOfTypeId, isInSelection } from "common/selection/helper/selection-helper";

type TypedId = stages.core.TypedId;

@Injectable({ providedIn: "root" })
export class SelectionStoreService {
	private readonly selectionRepository = inject(SelectionRepository);

	static defaultStoreIdentifier = "common";

	private createStateDynamic(storeName: string): void {
		this.selectionRepository.addStoreDynamic(storeName);
	}

	getStateDynamic(name: string): Store | undefined {
		return this.selectionRepository.getStoreDynamic(name);
	}

	hasStateDynamic(name: string): boolean {
		return this.selectionRepository.getStoreDynamic(name) !== undefined;
	}

	createAndInitializeStoreDynamic(
		storeName: string,
		allSelectables: Selectable[],
		initialSelections: Selectable[],
	): void {
		this.createStateDynamic(storeName);
		this.selectionRepository.setLoadingDynamic(storeName, true);

		const initialSelectedSelectables: Selectable[] = this.createSelectablesDynamic(allSelectables, initialSelections);
		this.selectionRepository.updateDynamic(storeName, initialSelectedSelectables);
		this.selectionRepository.setLoadingDynamic(storeName, false);
	}

	private createSelectablesDynamic(allSelectables: Selectable[], initialSelections: Selectable[]): Selectable[] {
		const selectablesToStore: Selectable[] = [];
		const parentIdsToUpdate: string[] = [];

		allSelectables.forEach((selectable) => {
			const isSelected = isInSelection(initialSelections, selectable);
			const selectableToSave: Selectable = {
				...selectable,
				typeId: selectable.typeId,
				initiallySelected: isSelected,
				isSelected: isSelected,
				wasUpdated: false,
				// indeterminate state is calculated later to increase performance
			};
			selectablesToStore.push(selectableToSave);
			// To update parents above the first level we need to hierarchically move upwards and set every parent to indeterminate
			if (isSelected && selectableToSave.parentId !== null) {
				parentIdsToUpdate.push(selectableToSave.parentId);
			}
		});
		this.updateAllParentsIndeterminateRecursiveDynamic(parentIdsToUpdate, selectablesToStore);
		return selectablesToStore;
	}

	createAndInitializeStoreCommon(initialSelections: Selectable[]): void {
		const initialSelectedSelectables: Selectable[] = this.createSelectablesCommon(initialSelections);
		this.selectionRepository.setLoadingCommon(true);
		emitOnce(() => {
			initialSelectedSelectables.forEach((initialSelectable) => {
				this.selectionRepository.addCommon(initialSelectable);
			});
		});
		this.selectionRepository.setLoadingCommon(false);
	}

	private createSelectablesCommon(selectables: Selectable[]): Selectable[] {
		// Even if the initialSelectables are provided we want to ensure correct initialization
		const selectablesToStore: Selectable[] = [];

		selectables.forEach((selectable) => {
			const selectableToSave: Selectable = {
				id: selectable.id,
				type: selectable.type,
				typeId: selectable.typeId,
				identity: selectable.storeIdentifier + "_" + selectable.typeId,
				parentId: selectable.parentId ?? null,
				parentType: selectable.type || null,
				storeIdentifier: selectable.storeIdentifier,
				isSelectable: selectable.isSelectable,
				initiallySelected: selectable.initiallySelected,
				indeterminate: selectable.indeterminate,
				selectableChildrenCount: null,
				initiallySelectedCount: null,
				totalChildrenCount: null,
				isSelected: true,
				comment: selectable.comment,
				isCommentEditable: selectable.isCommentEditable,
				wasUpdated: false,
			};
			selectablesToStore.push(selectableToSave);
		});
		return selectablesToStore;
	}

	updateAllParentsIndeterminateRecursiveDynamic(parentIds: string[], allSelected: Selectable[]): void {
		while (parentIds.length > 0) {
			const parentId = parentIds.pop();
			const parent = allSelected.find((selectable) => selectable.id === parentId);
			if (parent) {
				if (!parent.isSelected) {
					parent.indeterminate = true;
				}
				if (parent.parentId !== null) {
					parentIds.push(parent.parentId);
				}
			}
		}
	}

	selectAllByIdentifierCommon(storeIdentifier: string): void {
		this.selectionRepository.updateAllEntitiesCommon(storeIdentifier, true);
	}

	deselectAllByIdentifierCommon(storeIdentifier: string): void {
		this.selectionRepository.updateAllEntitiesCommon(storeIdentifier, false);
	}

	// TODO remove complexity

	addNewSelectableCommon(
		selectableTypedId: TypedId,
		shouldBeSelected: boolean,
		initiallySelected: boolean,
		explicitParentMode: boolean,
		isSelectable: boolean,
		parentTypeId?: TypedId,
		storeIdentifier?: string, // TODO make not optional
		indeterminate?: boolean,
		selectableChildrenCount?: number,
		initiallySelectedChildrenCount?: number,
		totalChildrenCount?: number,
	): void {
		const storeIdentifierWithDefault = storeIdentifier ?? SelectionStoreService.defaultStoreIdentifier;
		let parent: Selectable | undefined;
		if (parentTypeId) {
			parent = this.selectionRepository.getEntityCommon(getIdKeyOfTypeId(parentTypeId), storeIdentifierWithDefault);
			if (!explicitParentMode && !parent?.isSelected && !parent?.indeterminate) {
				shouldBeSelected = false;
			}
		}

		const selectableToSave: Selectable = {
			id: selectableTypedId.id,
			type: selectableTypedId.typeIdent,
			typeId: getIdKeyOfTypeId(selectableTypedId),
			identity: storeIdentifierWithDefault + "_" + getIdKeyOfTypeId(selectableTypedId),
			parentId: parent?.id ?? null,
			parentType: parent?.type ?? null,
			storeIdentifier: storeIdentifierWithDefault,
			isSelectable: isSelectable ?? true,
			initiallySelected: initiallySelected,
			indeterminate: indeterminate ?? false,
			selectableChildrenCount: selectableChildrenCount ?? null,
			initiallySelectedCount: initiallySelectedChildrenCount ?? null,
			totalChildrenCount: totalChildrenCount ?? null,
			isSelected: shouldBeSelected && isSelectable,
			comment: null,
			isCommentEditable: true,
			wasUpdated: false,
		};

		this.selectionRepository.addCommon(selectableToSave);
		if (parent) {
			this.setDirtyCommon(parent);
		}
	}

	selectCommon(selectable: Selectable, explicitParentMode: boolean): void {
		// we set wasUpdated to true, even if the selectable was initialliSelected aswell, because maybe a comment was added meanwhile
		this.selectionRepository.updateEntityCommon(selectable, {
			isSelected: true,
			wasUpdated: !selectable.initiallySelected,
			indeterminate:
				!!selectable.totalChildrenCount &&
				!!selectable.selectableChildrenCount &&
				selectable.totalChildrenCount > 0 &&
				selectable.totalChildrenCount > selectable.selectableChildrenCount,
		});

		this.selectIncludeChildrenCommon(selectable);
		if (selectable.parentId) {
			const parentTypedId = {
				typeIdent: selectable.parentType!,
				id: selectable.parentId,
			};
			const parent = this.selectionRepository.getEntityCommon(
				getIdKeyOfTypeId(parentTypedId),
				selectable.storeIdentifier,
			);
			if (parent) {
				this.setIndeterminate(parent, parent.isSelected, false, explicitParentMode);
				this.setDirtyCommon(parent);
			}
		}
	}

	deselectCommon(selectable: Selectable, explicitParentMode: boolean): void {
		this.selectionRepository.updateEntityCommon(selectable, {
			isSelected: false,
			indeterminate: false,
			wasUpdated: selectable.initiallySelected,
		});

		if (selectable.parentId) {
			const parentTypedId = {
				typeIdent: selectable.parentType!,
				id: selectable.parentId,
			};
			const parent = this.selectionRepository.getEntityCommon(
				getIdKeyOfTypeId(parentTypedId),
				selectable.storeIdentifier,
			);
			if (parent) {
				this.updateIsSelectedCommon(parent, false);
				this.setIndeterminate(parent, parent.isSelected, false, explicitParentMode);
				this.setDirtyCommon(parent);
			}
		}

		this.deselectChildrenCommon(selectable);
	}

	deselectChildrenCommon(selectable: Selectable): void {
		const directChildren = this.selectionRepository.getAllDirectChildrenCommon(selectable);
		if (directChildren) {
			directChildren.forEach((child: Selectable) => {
				this.updateIsSelectedCommon(child, false);
			});
		}
	}

	selectIncludeChildrenCommon(selectable: Selectable): void {
		const directChildren = this.selectionRepository.getAllDirectChildrenCommon(selectable);
		if (directChildren) {
			directChildren.forEach((child: Selectable) => {
				this.updateIsSelectedCommon(child, true);
			});
		}
	}

	selectDynamic(selectable: Selectable, explicitParentMode: boolean): void {
		if (selectable.isSelectable) {
			this.selectionRepository.updateEntityDynamic(selectable, {
				isSelected: true,
				wasUpdated: !selectable.initiallySelected,
				indeterminate: false,
			});
			this.handleIndeterminateForParentDynamic(selectable, explicitParentMode);
			if (selectable.parentId && this.selectionRepository.hasParentCommon(selectable.parentId)) {
				this.setDirtyCommon(selectable);
			}
		}
	}

	handleIndeterminateForParentDynamic(selectable: Selectable, explicitParentMode: boolean): void {
		if (selectable.parentId && this.selectionRepository.hasParentCommon(selectable.parentId)) {
			this.setDirtyCommon(selectable);
		}

		const parentId = getIdKeyOfTypeId({
			typeIdent: selectable.parentType ?? selectable.type,
			id: selectable.parentId ?? "no-id",
		});
		const parent = this.selectionRepository.getEntityDynamic(selectable.storeIdentifier, parentId);
		if (parent) {
			this.setIndeterminate(parent, parent.isSelected, true, explicitParentMode);
			this.handleIndeterminateForParentDynamic(parent, explicitParentMode);
		}
	}

	deselectDynamic(selectable: Selectable, explicitParentMode: boolean): void {
		emitOnce(() => {
			this.setIndeterminate(selectable, false, true, explicitParentMode);
			this.handleIndeterminateForParentDynamic(selectable, explicitParentMode);
		});
	}

	selectAllDynamic(storeName: string, parentTypeId: TypedId, leavesOnly: boolean, explicitParentMode: boolean): void {
		this.selectionRepository.setLoadingDynamic(storeName, true);
		const parentTarget = this.selectionRepository.getEntityDynamic(storeName, getIdKeyOfTypeId(parentTypeId));

		if (parentTarget) {
			emitOnce(() => {
				this.selectChildrenDynamic(storeName, parentTarget, leavesOnly, explicitParentMode);
				if (!leavesOnly || (leavesOnly && !this.hasChildrenDynamic(parentTarget, storeName))) {
					this.updateIsSelectedDynamic(parentTarget, true);
				}
			});
		}
		this.selectionRepository.setLoadingDynamic(storeName, false);
	}

	private hasChildrenDynamic(target: Selectable, storeName: string): boolean | undefined {
		const children = this.selectionRepository.getAllDirectChildrenDynamic(storeName, target);
		return !!children?.length;
	}

	selectChildrenDynamic(storeName: string, target: Selectable, leavesOnly: boolean, explicitParentMode: boolean): void {
		this.selectionRepository.setChildrenSelectedDynamic(storeName, target, leavesOnly);
		const directChildren = this.selectionRepository.getAllDirectChildrenDynamic(storeName, target);

		if (directChildren) {
			directChildren.forEach((child: Selectable) => {
				this.selectChildrenDynamic(storeName, child, leavesOnly, explicitParentMode);
			});
		}
		this.handleIndeterminateForParentDynamic(target, explicitParentMode);
	}

	deselectChildrenDynamic(storeName: string, target: Selectable, explicitParentMode: boolean): void {
		this.selectionRepository.setChildrenDeSelectedDynamic(storeName, target);
		const directChildren = this.selectionRepository.getAllDirectChildrenDynamic(storeName, target);

		if (directChildren) {
			directChildren.forEach((child: Selectable) => {
				this.deselectChildrenDynamic(storeName, child, explicitParentMode);
			});
		}
		this.handleIndeterminateForParentDynamic(target, explicitParentMode);
	}

	deselectAllDynamic(storeName: string, parentTypeId: TypedId, explicitParentMode: boolean): void {
		this.selectionRepository.setLoadingDynamic(storeName, true);

		const parentTarget = this.selectionRepository.getEntityDynamic(storeName, getIdKeyOfTypeId(parentTypeId));
		if (parentTarget) {
			emitOnce(() => {
				this.selectionRepository.updateEntityDynamic(parentTarget, { isSelected: false });
				// remove all should also deselect the actual node (parent) to show the user it is deselected
				this.deselectChildrenDynamic(storeName, parentTarget, explicitParentMode);
			});
		}
		this.selectionRepository.setLoadingDynamic(storeName, false);
	}

	updateCommentCommon(selectable: Selectable, newComment: string): void {
		this.selectionRepository.updateEntityCommon(selectable, { comment: newComment, wasUpdated: true });
	}

	private updateIsSelectedCommon(selectable: Selectable, shouldBeSelected: boolean): void {
		if (selectable.isSelectable) {
			this.selectionRepository.updateEntityCommon(selectable, {
				isSelected: shouldBeSelected && selectable.isSelectable,
				wasUpdated: selectable.initiallySelected !== shouldBeSelected,
			});
		}
	}

	private updateIsSelectedDynamic(selectable: Selectable, shouldBeSelected: boolean): void {
		if (selectable.isSelectable) {
			this.selectionRepository.updateEntityDynamic(selectable, {
				isSelected: shouldBeSelected,
				wasUpdated: selectable.initiallySelected !== shouldBeSelected,
				indeterminate: shouldBeSelected ? false : selectable.indeterminate,
			});
		}
	}

	private setIndeterminate(
		element: Selectable,
		shouldBeSelected: boolean,
		forDynamicStore: boolean,
		explicitParentMode: boolean,
	): void {
		const directSelectableChildren = forDynamicStore
			? this.selectionRepository
					.getAllDirectChildrenDynamic(element.storeIdentifier, element)
					?.filter((child) => child.isSelectable)
			: this.selectionRepository.getAllDirectChildrenCommon(element)?.filter((child) => child.isSelectable);

		const hasSelectedOrIndeterminateChildren = !!directSelectableChildren?.find(
			(child) => child.isSelected || child.indeterminate,
		);
		const hasDeselectedChildren = !!directSelectableChildren?.find((child) => !child.isSelected);

		const selected = explicitParentMode
			? shouldBeSelected
			: hasSelectedOrIndeterminateChildren && !hasDeselectedChildren;
		const indeterminate = explicitParentMode
			? !shouldBeSelected && hasSelectedOrIndeterminateChildren
			: hasSelectedOrIndeterminateChildren && hasDeselectedChildren;

		if (forDynamicStore) {
			this.selectionRepository.updateEntityDynamic(element, {
				wasUpdated: element.indeterminate !== hasSelectedOrIndeterminateChildren,
				indeterminate: indeterminate,
				isSelected: selected,
			});
		} else {
			this.selectionRepository.updateEntityCommon(element, {
				wasUpdated: element.indeterminate !== hasSelectedOrIndeterminateChildren && hasDeselectedChildren,
				indeterminate: indeterminate,
				isSelected: selected,
			});
		}
	}

	setDirtyCommon(selectable: Selectable): void {
		this.selectionRepository.updateEntityCommon(selectable, { wasUpdated: true });
	}

	setLoadingDynamic(storeName: string, value: boolean): void {
		this.selectionRepository.setLoadingDynamic(storeName, value);
	}

	resetCommon(): void {
		this.selectionRepository.resetCommon();
	}

	deleteDynamic(storeName: string): void {
		this.selectionRepository.deleteDynamic(storeName);
	}

	setSelectAllPressed(isActivated: boolean): void {
		this.selectionRepository.setSelectAllPressedCommon(isActivated);
	}
}
